import lockIcon from '../assets/icons/lock.png';
import externalIcon from '../assets/icons/external.png';
import ActionButton from "../Components/ActionButton";
import Breadcrumbs from "../Components/Breadcrumbs";
import {mapRoutesEnToSv} from "../Utils/routes";
import trendDarkIcon from "../assets/icons/trend-dark.svg";
import trendDownDarkIcon from "../assets/icons/trend-down-dark.svg";
import trendUpDarkIcon from "../assets/icons/trend-up-dark.svg";
import {useEffect, useRef, useState} from "react";

function ContentAction({action, background}: any) {
    const hasBorder = action.template === 'light' && background === 'grey';
    return (
        <ActionButton href={action.type === 'external' ? action.url : mapRoutesEnToSv(action.url)} external={action.type === 'external'} css={`xl:me-auto ${action.template === 'dark' ? 'bg-grey-900 text-white' : 'bg-grey-100 text-grey-900'} ${hasBorder ? 'border border-grey-900' : ''}`}>
            {action.title}
            {action.type === 'external' && <img src={externalIcon} width="24" height="24"/>}
        </ActionButton>
    );
}

function Trend({value}: any) {
    let src = trendDarkIcon
    if (value < -0.1) {
        src = trendDownDarkIcon;
    } else if (value > 0.1) {
        src = trendUpDarkIcon;
    } else if (value === null) {
        return <></>;
    }

    return <img src={src} className="mx-auto" width="38" height="38" />
}

function RelatedTitles({titles}: any) {
    const [showAll, setShowAll] = useState(false);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const [initialOverflowCheck, setInitialOverflowCheck] = useState(false);
    const textRef = useRef<HTMLParagraphElement>(null);

    const titlesText = titles ? titles.join(', ') : '';
    useEffect(() => {
        const checkOverflow = () => {
            if (textRef.current && !showAll) {
                const element = textRef.current;
                const hasOverflow = element.scrollHeight > element.clientHeight;
                setIsOverflowing(hasOverflow);
                setInitialOverflowCheck(true);
            }
        };

        const timeoutId = setTimeout(checkOverflow, 100);

        const resizeHandler = () => {
            clearTimeout(timeoutId);
            setTimeout(checkOverflow, 100);
        };
        window.addEventListener('resize', resizeHandler);

        return () => {
            clearTimeout(timeoutId);
            window.removeEventListener('resize', resizeHandler);
        };
    }, [titlesText, showAll]);

    if (!titles || titles.length === 0) {
        return null;
    }

    const shouldShowButton = (initialOverflowCheck && isOverflowing) || showAll;

    return (
        <div className="mt-2 py-8 border-t border-grey-500">
            <h3 className="font-body text-xl text-grey-800 mb-1">Vanliga roller inom detta yrke</h3>
            <div className="mb-4">
                <p ref={textRef} className={`font-body text-grey-900 ${showAll ? '' : 'line-clamp-2'}`}>
                    {titlesText}
                </p>
            </div>
            {shouldShowButton && (
                <div className="flex justify-end">
                    <button
                        className="font-body font-bold underline hover:no-underline focus:outline-none focus:underline"
                        onClick={() => setShowAll(!showAll)}
                    >
                        {showAll ? 'Visa färre' : 'Visa alla'}
                    </button>
                </div>
            )}
        </div>
    );
}

function ContentSection({content, isHero}: any) {
    const bgClass = content.background === 'white' ? 'bg-white' : 'bg-grey-200';
    const breadcrumbsLength = content.breadcrumbs?.length;
    const hasContent = content.image || content.fact || content.tag || content.title || content.text || content.button || content.amount;

    return (
        <>
            {breadcrumbsLength && <Breadcrumbs links={content.breadcrumbs} background={bgClass}/>}
            {hasContent &&
                <div className={`w-full pb-8 xl:pb-16 border-b border-grey-400 ${bgClass} ${breadcrumbsLength ? 'pt-0 xl:pt-0' : 'pt-8 xl:pt-16'} ${breadcrumbsLength === 2 ? '!pt-8 xl:!pt-0' : ''}`}>
                    <div className="container max-w-3xl xl:flex xl:flex-row-reverse xl:items-center xl:gap-6">
                        {content.image &&
                            <img
                                src={content.image}
                                alt={content.title}
                                className="block w-full aspect-[5/3] object-cover mb-4 xl:mb-0 xl:aspect-square xl:flex-1 xl:w-1/3"
                            />
                        }

                        {content.fact && !content.image &&
                            <p className="font-sweing text-8xl text-rose w-1/3 hidden xl:flex xl:justify-center xl:items-center xl:flex-1">{content.fact}</p>
                        }

                        <div className={`flex flex-col gap-4 xl:flex-2 ${isHero ? 'xl:gap-8' : 'xl:gap-4'}`}>
                            {content.tag &&
                                <div className="flex jusify-start items-baseline">
                                    {content.tag === 'FÖR MEDLEMMAR' && <img src={lockIcon} width="20" height="20" alt="Lock icon"/>}
                                    <p className="font-sweing font-medium uppercase text-base text-rose ms-2">{content.tag}</p>
                                </div>
                            }

                            {content.title &&
                                <h2 className={`font-sweing font-medium text-black ${isHero ? 'text-4xl xl:text-6xl' : 'text-3xl xl:text-5xl'}`}
                                    dangerouslySetInnerHTML={{__html: content.title.replaceAll(' ', ' <wbr>').replaceAll('/', '/<wbr>')}}/>
                            }

                            {content.text &&
                                <p className="font-body text-lg xl:text-xl text-black">{content.text}</p>
                            }

                            {content.fact &&
                                <p className={`font-sweing text-7xl xl:text-8xl mb-2 text-rose ${!content.image ? 'xl:hidden' : ''}`}>{content.fact}</p>
                            }

                            {content.amount &&
                                <div className="flex items-center gap-4 mt-2">
                                    <div className="flex flex-1 gap-1.5 items-center">
                                        <p className="font-sweing text-7xl xl:text-[55px] text-rose leading-none">{content.amount}</p>
                                        <p className="font-body text-[12px] text-grey-700 leading-[1.2]">Jobbannonser senaste<br />12 månader</p>
                                    </div>
                                    {content.trend &&
                                        <div className="flex flex-1 gap-1.5 items-center">
                                            <p className="leading-none"><Trend value={content.trend}/></p>
                                            <p className="font-body text-[12px] text-grey-700 leading-[1.2]">Förändring<br />12 månader</p>
                                        </div>
                                    }
                                </div>
                            }

                            {content.relatedTitles &&
                                <RelatedTitles titles={content.relatedTitles} />
                            }

                            {content.button &&
                                <ContentAction action={content.button} background={content.background}/>
                            }
                        </div>
                    </div>
                </div>
            }
        </>
    );
}

export default ContentSection;